import { isDay, readRange, type ReportDay, type ReportRange } from "@ewn/shared";
import { readReport } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { useUrlParams } from "@/lib/useUrlParam";
import { money } from "@/lib/format";
import { PRESET_LABEL, PRESETS, presetOf, presetRange } from "@/lib/report-range";
import { FilterChips } from "@/components/FilterChips";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Kpi } from "@/components/ui/kpi";
import { PageHeader } from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";
import { statusBadge } from "@/screens/OrdersScreen";

/**
 * Sales over a range of Istanbul days: the money, the days, the packages and
 * the coins it came in (`worker/src/domain/admin-report.ts` says what each
 * figure counts).
 *
 * The range lives in the URL, so a report can be sent to a colleague as a
 * link. A range the URL does not hold, or holds wrong, is the last 30 days.
 */
export function StatsScreen() {
  const [param, setParams] = useUrlParams<"from" | "to">();
  const now = new Date();
  const asked = readRange(param("from"), param("to"));
  const range = asked.ok ? asked.range : presetRange("30d", now);
  const preset = presetOf(range, now);
  const read = useAsync(() => readReport(range), [range.from, range.to]);
  const { loading, error } = read;
  // Only the answer to the range on screen: while another range loads, and
  // after its read fails, the answer kept is the last range's. Nothing reads
  // the same range twice, so the range alone tells the two apart.
  const answered = read.data?.range;
  const data = answered?.from === range.from && answered.to === range.to ? read.data : null;

  const net = data ? data.revenueCents - data.refundedCents : 0;
  const setRange = (next: ReportRange) => setParams({ from: next.from, to: next.to });

  return (
    <div className="space-y-4">
      <PageHeader title="Stats" description="Sales by Istanbul day: money, packages and coins." />

      <div className="flex flex-wrap items-center gap-1.5">
        <FilterChips
          options={PRESETS.map((value) => ({ value, label: PRESET_LABEL[value] }))}
          value={preset ?? ""}
          onChange={(next) => setRange(presetRange(next, now))}
        />
        <Input
          size="sm"
          type="date"
          className="w-40"
          aria-label="From"
          value={range.from}
          max={range.to}
          onChange={(e) => isDay(e.target.value) && setRange({ ...range, from: e.target.value })}
        />
        <Input
          size="sm"
          type="date"
          className="w-40"
          aria-label="To"
          value={range.to}
          min={range.from}
          onChange={(e) => isDay(e.target.value) && setRange({ ...range, to: e.target.value })}
        />
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Revenue"
          value={money(data?.revenueCents)}
          hint={data ? `${data.paidOrders} paid orders` : undefined}
        />
        <Kpi
          label="Refunded"
          value={money(data?.refundedCents)}
          hint={data ? `${data.refunds} refunds` : undefined}
        />
        <Kpi label="Supplier cost" value={money(data?.costCents)} hint="Orders bought upstream" />
        <Kpi
          label="Margin"
          value={money(data ? net - data.costCents : undefined)}
          hint="Revenue less refunds and cost"
        />
        <Kpi
          label="Average order"
          value={money(
            data && data.paidOrders > 0 ? data.revenueCents / data.paidOrders : undefined,
          )}
        />
        <Kpi
          label="Top-ups"
          value={data?.topups ?? "—"}
          hint={data ? money(data.topupRevenueCents) : undefined}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue by day</CardTitle>
        </CardHeader>
        <div className="px-4 pb-4">
          {data ? <DayChart days={data.days} /> : <div className="h-40" />}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <DataTable
          title="By coin"
          columns={4}
          loading={loading}
          empty="Nothing was paid in this range."
          rows={data?.coins ?? []}
          head={
            <>
              <TH>Coin</TH>
              <TH numeric>Orders</TH>
              <TH numeric>Revenue</TH>
              <TH numeric>Share</TH>
            </>
          }
          renderRow={(coin) => (
            <TR key={`${coin.ticker}|${coin.network}`}>
              <TD>
                {coin.ticker} <span className="text-muted-foreground">· {coin.network}</span>
              </TD>
              <TD numeric>{coin.orders}</TD>
              <TD numeric>{money(coin.revenueCents)}</TD>
              <TD numeric>{share(coin.revenueCents, data?.revenueCents)}</TD>
            </TR>
          )}
        />
        <DataTable
          title="Orders opened, by where they stand"
          columns={2}
          loading={loading}
          empty="No order was opened in this range."
          rows={Object.entries(data?.openedByStatus ?? {}).sort((a, b) => b[1] - a[1])}
          head={
            <>
              <TH>Status</TH>
              <TH numeric>Orders</TH>
            </>
          }
          renderRow={([status, count]) => (
            <TR key={status}>
              <TD>{statusBadge(status)}</TD>
              <TD numeric>{count}</TD>
            </TR>
          )}
        />
      </div>

      <DataTable
        title="By package"
        columns={5}
        loading={loading}
        empty="Nothing was paid in this range."
        rows={data?.packages ?? []}
        head={
          <>
            <TH>Package</TH>
            <TH numeric>Orders</TH>
            <TH numeric>eSIMs</TH>
            <TH numeric>Revenue</TH>
            <TH numeric>Cost</TH>
          </>
        }
        renderRow={(pkg) => (
          <TR key={pkg.packageId}>
            <TD>{pkg.packageId}</TD>
            <TD numeric>{pkg.orders}</TD>
            <TD numeric>{pkg.quantity}</TD>
            <TD numeric>{money(pkg.revenueCents)}</TD>
            <TD numeric>{money(pkg.costCents)}</TD>
          </TR>
        )}
      />
    </div>
  );
}

/** A part of a whole, as a whole percent; a dash for no whole. */
function share(part: number, whole: number | undefined): string {
  return whole ? `${Math.round((part / whole) * 100)}%` : "—";
}

/** Each day's slot in the drawing; a bar fills all of it but the gap. */
const SLOT = 10;
const BAR = 8;
const PLOT_HEIGHT = 100;

/**
 * One bar a day, on one scale: the tallest day's revenue. Each bar names its
 * day and sum when pointed at. The bars stretch to the card's width, so the
 * day labels sit under the drawing rather than in it, where they would
 * stretch too.
 */
function DayChart({ days }: { days: ReportDay[] }) {
  const peak = Math.max(...days.map((day) => day.revenueCents), 0);
  if (peak === 0) {
    return <div className="py-12 text-center text-sm text-muted-foreground">No sales.</div>;
  }
  return (
    <div className="space-y-1">
      <svg
        role="img"
        aria-label={`Revenue by day, highest ${money(peak)}`}
        viewBox={`0 0 ${days.length * SLOT} ${PLOT_HEIGHT}`}
        preserveAspectRatio="none"
        className="h-40 w-full"
      >
        {days.map((day, i) => {
          const height = (day.revenueCents / peak) * PLOT_HEIGHT;
          return (
            <rect
              key={day.day}
              x={i * SLOT + (SLOT - BAR) / 2}
              y={PLOT_HEIGHT - height}
              width={BAR}
              height={height}
              className="fill-primary"
            >
              <title>{`${day.day}: ${money(day.revenueCents)}, ${day.orders} orders`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="flex justify-between text-xs text-muted-foreground nums">
        <span>{days[0]?.day}</span>
        <span>highest {money(peak)}</span>
        <span>{days[days.length - 1]?.day}</span>
      </div>
    </div>
  );
}
